import { Injectable } from "@angular/core";
import { HttpHeaders, HttpParams } from "@angular/common/http";
import { CookieService } from "ngx-cookie-service";
import { ApiService } from "@app/services/api/api.service";

/** Cookie holding the Super Admin's own token while an impersonated session is active. */
const IMPERSONATOR_TOKEN = "impersonator_token";
/** Cookie holding the name shown in the "you are impersonating X" banner. */
const IMPERSONATED_NAME = "impersonated_name";
export interface ImpersonationLocation {
  latitude: number;
  longitude: number;
  accuracy: number;
}

/**
 * Cookie holding when the impersonation token stops being accepted, as epoch milliseconds on
 * *this browser's* clock.
 *
 * Deliberately not the API's "Y-m-d H:i:s" string: that is in the server's timezone
 * (America/Phoenix), and Date parses a bare timestamp as browser-local time. For any browser
 * ahead of the server the deadline landed in the past, and the banner ended the session the
 * moment it started.
 */
const IMPERSONATION_EXPIRES = "impersonation_expires_at";
const PENDING_LOCATION = "impersonation_pending_location";
const LOCATION_RETRY = "retry";

@Injectable({
  providedIn: "root",
})
export class SuperAdminService {
  constructor(
    private apiService: ApiService,
    private cookieService: CookieService
  ) {}

  getPanelOverview() {
    return this.apiService.get("api/admin/panel");
  }

  getCompanies(query: Record<string, any> = {}) {
    return this.apiService.get("api/admin/companies", this.toParams(query));
  }

  getCompany(id: number) {
    return this.apiService.get(`api/admin/companies/${id}`);
  }

  getUsers(query: Record<string, any> = {}) {
    return this.apiService.get("api/admin/users", this.toParams(query));
  }

  getRoles() {
    return this.apiService.get("api/admin/users/roles");
  }

  getFailedJobs(query: Record<string, any> = {}) {
    return this.apiService.get("api/admin/jobs/failed", this.toParams(query));
  }

  retryFailedJobs(body: { ids?: number[]; all?: boolean }) {
    return this.apiService.post("api/admin/jobs/failed/retry", body);
  }

  deleteFailedJobs(body: { ids?: number[]; all?: boolean }) {
    return this.apiService.delete("api/admin/jobs/failed", { body });
  }

  /** Operational monitoring built from data the application already records. */
  getMonitor() {
    return this.apiService.get("api/admin/monitor");
  }

  getSystemHealth() {
    return this.apiService.get("api/admin/system");
  }

  getSystemLogs(query: Record<string, any> = {}) {
    return this.apiService.get("api/admin/system/logs", this.toParams(query));
  }

  clearCache(targets: string[]) {
    return this.apiService.post("api/admin/system/cache", { targets });
  }

  /**
   * Changes the signed-in Super Admin's own password. There is no user id to pass: the endpoint
   * only ever acts on whoever is authenticated, so it cannot be aimed at another account.
   */
  updateOwnPassword(body: {
    actual: string;
    password: string;
    password_confirmation: string;
  }) {
    return this.apiService.put("api/admin/account/password", body);
  }

  impersonate(userId: number, location: ImpersonationLocation | null = null) {
    return this.apiService.post(
      `api/admin/users/${userId}/impersonate`,
      location ?? {}
    );
  }

  /**
   * Where the Super Admin is starting the session from, for the audit trail.
   *
   * Never rejects: a denied permission, a blocked prompt or a slow fix must not stop support
   * work, so the caller simply gets null and the session is recorded without a location.
   * Requires a secure context, so on production this only resolves over HTTPS.
   */
  captureLocation(timeoutMs = 8000): Promise<ImpersonationLocation | null> {
    if (!navigator?.geolocation) return Promise.resolve(null);

    return new Promise((resolve) => {
      navigator.geolocation.getCurrentPosition(
        (position) =>
          resolve({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy,
          }),
        () => resolve(null),
        { timeout: timeoutMs, maximumAge: 60000, enableHighAccuracy: false }
      );
    });
  }

  /**
   * Whether this browser will hand over a location, so the reason a session has no coordinates
   * can be explained instead of just going missing.
   *
   * Geolocation cannot be forced: "denied" is a decision only the person can reverse, in the
   * browser's site settings.
   */
  geolocationPermission(): Promise<PermissionState | "unsupported"> {
    if (!navigator?.geolocation) return Promise.resolve("unsupported");
    if (!navigator?.permissions?.query) return Promise.resolve("prompt");

    return navigator.permissions
      .query({ name: "geolocation" as PermissionName })
      .then((status) => status.state)
      .catch(() => "prompt" as PermissionState);
  }

  /** One row per impersonation session: who signed in as whom, and when. */
  getImpersonationLogs(query: Record<string, any> = {}) {
    return this.apiService.get("api/admin/impersonation/logs", this.toParams(query));
  }

  /** What was done inside one session. */
  getSessionActivity(sessionId: string, query: Record<string, any> = {}) {
    return this.apiService.get(
      `api/admin/impersonation/logs/${encodeURIComponent(sessionId)}`,
      this.toParams(query)
    );
  }

  /**
   * Impersonation tokens that are still usable right now. `silent` is for the periodic
   * refresh, which should not run the loading bar every 30 seconds.
   */
  getImpersonationSessions(silent = false) {
    return this.apiService.get(
      "api/admin/impersonation/sessions",
      undefined,
      silent ? new HttpHeaders({ skipLoader: "true" }) : undefined
    );
  }

  /** Ends live sessions from the admin panel, before their 30 minutes are up. */
  revokeImpersonationSessions(body: { token_ids?: string[]; all?: boolean }) {
    return this.apiService.post("api/admin/impersonation/sessions/revoke", body);
  }

  /** Revokes the impersonation token server-side, using the impersonated session's own token. */
  endImpersonationSession() {
    return this.apiService.post("api/impersonation/stop", {});
  }

  attachImpersonationLocation(location: ImpersonationLocation) {
    // Background bookkeeping: the API's "Location recorded." is not worth a toast.
    return this.apiService.post(
      "api/impersonation/location",
      location,
      new HttpHeaders({ skipMessage: "true" })
    );
  }

  setPendingLocation(location: ImpersonationLocation | null) {
    this.cookieService.set(
      PENDING_LOCATION,
      location ? JSON.stringify(location) : LOCATION_RETRY,
      { path: "/" }
    );
  }

  takePendingLocation(): ImpersonationLocation | null | undefined {
    const raw = this.cookieService.get(PENDING_LOCATION);
    this.cookieService.delete(PENDING_LOCATION, "/");

    if (!raw) return undefined;
    if (raw === LOCATION_RETRY) return null;

    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }

  /** Authoritative time left, straight from the token the API is holding. */
  getImpersonationStatus() {
    return this.apiService.get("api/impersonation/status");
  }

  /** Re-anchors the local deadline to what the server reports, in case this clock is off. */
  syncImpersonationDeadline(secondsRemaining: number) {
    this.cookieService.set(
      IMPERSONATION_EXPIRES,
      String(Date.now() + (Number(secondsRemaining) || 0) * 1000),
      { path: "/" }
    );
  }

  /**
   * Swap the active session for the impersonated one, keeping the Super Admin token so the
   * banner in the layout can put it back without a second login.
   */
  /** `expiresInSeconds` is the API's `expires_in`, which carries no timezone to get wrong. */
  startImpersonation(
    accessToken: string,
    displayName: string,
    expiresInSeconds: number
  ) {
    const deadline = Date.now() + (Number(expiresInSeconds) || 0) * 1000;

    this.cookieService.set(IMPERSONATOR_TOKEN, this.cookieService.get("token"), {
      path: "/",
    });
    this.cookieService.set(IMPERSONATED_NAME, displayName, { path: "/" });
    this.cookieService.set(IMPERSONATION_EXPIRES, String(deadline), { path: "/" });
    this.cookieService.set("token", accessToken, { path: "/" });
  }

  /**
   * Put the Super Admin's own token back. Returns false when there is nothing to restore, which
   * is the signal to fall through to the normal logout path.
   */
  restoreImpersonatorSession(): boolean {
    const original = this.cookieService.get(IMPERSONATOR_TOKEN);

    if (original) {
      this.cookieService.set("token", original, { path: "/" });
    }

    this.cookieService.delete(IMPERSONATOR_TOKEN, "/");
    this.cookieService.delete(IMPERSONATED_NAME, "/");
    this.cookieService.delete(IMPERSONATION_EXPIRES, "/");
    this.cookieService.delete(PENDING_LOCATION, "/");

    return !!original;
  }

  get isImpersonating(): boolean {
    return this.cookieService.check(IMPERSONATOR_TOKEN);
  }

  get impersonatedName(): string {
    return this.cookieService.get(IMPERSONATED_NAME);
  }

  /**
   * Seconds left on the impersonation token, or null when nothing is being impersonated. The
   * server is the authority on expiry; this only drives the countdown shown in the banner.
   */
  get impersonationSecondsRemaining(): number | null {
    const deadline = Number(this.cookieService.get(IMPERSONATION_EXPIRES));

    // Returning null rather than 0 matters: the caller ends the session on 0, so an unreadable
    // or missing deadline must mean "do nothing" instead of "expired".
    if (!Number.isFinite(deadline) || deadline <= 0) return null;

    return Math.max(0, Math.round((deadline - Date.now()) / 1000));
  }

  /** Drops empty values so the API never receives `search=` or `role=undefined`. */
  private toParams(query: Record<string, any>): HttpParams {
    let params = new HttpParams();

    Object.keys(query).forEach((key) => {
      const value = query[key];
      if (value !== null && value !== undefined && value !== "") {
        params = params.set(key, String(value));
      }
    });

    return params;
  }
}
